import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Compass, CheckCircle, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(token ? "" : "Missing reset token.");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (pw !== pw2) { setError("Passwords don't match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => nav("/login"), 1800);
    } catch (err) {
      setError(err.response?.data?.detail || "Reset failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col" data-testid="reset-password-page">
      <div className="px-6 py-6">
        <Link to="/" className="inline-flex items-center gap-2" data-testid="back-home">
          <Compass size={22} weight="duotone" className="text-blue-500" />
          <span className="font-medium tracking-tight">Career GPS <span className="text-blue-500">AI</span></span>
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 pb-12">
        <div className="w-full max-w-md">
          {done ? (
            <div className="bg-[#0F0F12] border border-emerald-500/30 rounded-2xl p-8 text-center" data-testid="reset-done-state">
              <CheckCircle size={48} weight="fill" className="mx-auto text-emerald-400 mb-3" />
              <h1 className="text-2xl font-medium">Password updated</h1>
              <p className="text-zinc-500 mt-3">Redirecting you to login…</p>
            </div>
          ) : (
            <>
              <h1 className="text-4xl tracking-tight font-medium">Choose a new password</h1>
              <p className="text-zinc-500 mt-2">Make it at least 6 characters.</p>

              <form onSubmit={submit} className="mt-8 space-y-4">
                <div>
                  <label className="text-xs text-zinc-400 uppercase tracking-widest">New password</label>
                  <input type="password" required value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-pw-input"
                    className="mt-1 w-full bg-[#0F0F12] border border-white/10 focus:border-blue-500 rounded-md px-4 py-3 text-white outline-none" />
                </div>
                <div>
                  <label className="text-xs text-zinc-400 uppercase tracking-widest">Confirm password</label>
                  <input type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="reset-pw2-input"
                    className="mt-1 w-full bg-[#0F0F12] border border-white/10 focus:border-blue-500 rounded-md px-4 py-3 text-white outline-none" />
                </div>
                {error && (
                  <div className="text-sm text-red-400 flex items-center gap-2" data-testid="reset-error">
                    <XCircle size={16} weight="fill" /> {error}
                  </div>
                )}
                <button type="submit" disabled={loading || !token} data-testid="reset-submit-btn"
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-3 rounded-lg transition-colors">
                  {loading ? "Updating..." : "Update password"}
                </button>
              </form>
              <p className="mt-6 text-sm text-zinc-500 text-center">
                <Link to="/forgot-password" className="text-blue-400 hover:text-blue-300" data-testid="request-new-link">Request a new link</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
